import React, { useState, useContext, useRef, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Stack,
  Chip,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Checkbox,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  Popover,
  TextField,
} from '@mui/material';
import FlagIcon from '@mui/icons-material/Flag';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import * as actions from '../../redux/actions';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';
import PopupDragContext from './PopupDragContext';
import {
  getPaidValue,
  getExpectedValue,
  getProjectedFund,
  getBgColumnValues,
  getRefundPendingValue,
} from '../../components/Table/projectDashboard/formulas';
import { REPORT_OPTIONS } from '../../components/Table/projectDashboard/constants';
import { DevWinBadge } from '../../components/DevPageBadge';

import {
  ShowFormulaContext,
  ColumnWidthContext,
  EditableFieldRegistryContext,
} from './projectSingleDashboard/contexts';
import {
  requestAutoFocusFirstField,
  consumeAutoFocusFirstField,
} from './projectSingleDashboard/focusChain';
import {
  THAI_WEEKDAYS,
  WORK_OPTIONS_EXPANDED,
  FINANCE_OPTIONS,
  BG_USAGE_OPTIONS,
  NO_BG_USAGE_KEY,
  LEGAL_OPTIONS,
  WARRANTY_COLOR,
  PCT_COL_W,
  PCT_DEFAULT_CH,
  SHARED_VALUE_WIDTH_PX,
} from './projectSingleDashboard/constants';
import {
  computeHasCommitment,
  computeStatusCascade,
  computeFinanceSync,
  computeWorkAutoBump,
  computeWorkAutoRevert,
  fmt,
  fmtPct,
  fmtDate,
  toDateInput,
  getExpandedWidthFromDefault,
  getLongestText,
} from './projectSingleDashboard/helpers';
import { StatusGroup } from './projectSingleDashboard/components/StatusGroup';
import { Row, EditableRow } from './projectSingleDashboard/components/Row';
import { SectionHeader } from './projectSingleDashboard/components/SectionHeader';
import { TruncatedTooltipText } from './projectSingleDashboard/components/TruncatedTooltipText';
import { NoteBox } from './projectSingleDashboard/components/NoteBox';
import { POProgressBars } from './projectSingleDashboard/components/POProgressBars';

// label ไทยของ Expense.status (backend/models/Expenses.js) — ใช้ตอน export excel รายการเบิกจ่าย
const EXPENSE_STATUS_LABEL = {
  PENDING: 'รอดำเนินการ',
  PREAPPROVE: 'รออนุมัติอีกครั้ง',
  APPROVE: 'อนุมัติแล้ว',
  SUCCESS: 'สำเร็จ',
  REJECT: 'ไม่อนุมัติ',
  PREPARE: 'เตรียมข้อมูล',
  CANCEL: 'ยกเลิก',
};

// Dashboard สรุปเฉพาะโปรเจกต์เดียว — แก้ไขได้ทุกช่องที่ P5 (ProjectDashboardTable.js) แก้ไขได้
// สูตรการเงิน/cascade สถานะ ใช้ฟังก์ชันเดียวกับ P5 เป๊ะ และ patch ไปที่ backend endpoint เดียวกัน
// (projectPatchDashboard) แล้วเรียก onSaved เพื่อให้หน้า P5 รีเฟรชข้อมูลตาม
//
// โครงสร้างไฟล์ — แยกออกมาจากไฟล์เดียว 1,989 บรรทัดเดิม (session ที่แยกไฟล์) เลียนแบบโฟลเดอร์
// projectDashboard/ ของ P5: contexts.js, constants.js, helpers.js, components/*.js อยู่ใน
// ./projectSingleDashboard/ — ไฟล์นี้เหลือแค่ตัว component หลักที่ประกอบชิ้นส่วนเข้าด้วยกัน
export default function ProjectSingleDashboard({
  project: p,
  projectId,
  uiLang = 'en',
  onLocalUpdate,
  onSaved,
  onPopIn,
  onPrev,
  onNext,
  navIndex, // ลำดับ (0-based) ของโปรเจกต์นี้ในลิสต์ที่กรอง/เรียงอยู่ใน P5 ตอนนั้น
  navTotal, // จำนวนทั้งหมดในลิสต์เดียวกัน
  filterChips, // ป้ายตัวกรองที่ active อยู่ใน P5 ตอนนั้น (read-only แสดงบริบท)
  onOpenExpenseDetail, // เปิด dialog รายละเอียดตั้งเบิก (P-113) จาก ProjectPopup.js — ใช้ตอน
  // dblclick รายการใน popup "รายการตั้งเบิกที่รอจ่าย" (row 9, P-147)
  onOpenRefundModal, // เปิด RefundItemsModal (P-151) ของงบย่อยนั้นๆ จาก ProjectPopup.js — ใช้ตอน
  // dblclick รายการใน popup "รอเงินคืน" breakdown (row 13, P-152)
  onOpenPendingModal, // เปิด PendingItemsModal (P-150) ของงบย่อยนั้นๆ จาก ProjectPopup.js — ใช้ตอน
  // dblclick รายการใน popup "AP PENDING ตามงบย่อย" (row 10, P-148)
}) {
  const dispatch = useDispatch();
  const U = (th, en) => (uiLang === 'en' ? en : th);
  const me = useSelector((state) => state.me);
  const authorName = formatEmployeeThaiName(me?.userData);
  // มีค่าเฉพาะตอนอยู่ใน modal ที่ลากย้ายตำแหน่งได้ (ครอบด้วย DraggablePaper ใน ProjectList.js)
  // New Window ไม่มี provider นี้ครอบ เลยเป็น null — ไม่มีอะไรให้ recenter
  const recenterPopup = useContext(PopupDragContext);

  const applyPatch = async (patch) => {
    onLocalUpdate?.(patch);
    try {
      await dispatch(actions.projectPatchDashboard(projectId, patch));
      // ดึงข้อมูลจริงจาก server ซ้ำอีกที — กัน race condition กรณี projectGet(id) ตอนเปิด
      // popup ครั้งแรกยังโหลดไม่เสร็จแล้วมาทับค่าที่เพิ่ง patch ไปทีหลัง (โน้ตหาย/ค่าเด้งกลับ)
      // ใช้ return value ตรงๆ (ไม่พึ่ง state.project ที่ projectAll เขียนทับได้) กัน race อีกชั้น
      const fresh = await dispatch(actions.projectGet(projectId));
      if (fresh) onLocalUpdate?.(fresh);
      onSaved?.();
      return fresh || true;
    } catch (err) {
      console.error('แก้ไข dashboard ไม่สำเร็จ', err);
      return false;
    }
  };

  const handleStatusSelect = (field, value) => {
    const patch = computeStatusCascade(p, field, value);
    if (patch) applyPatch(patch);
  };

  // ด้านงาน: ตัวเลือก "ดำเนินการ" แตกเป็น 3 วิธี (ทำเอง/จ้างเหมา/ผสม) — encode รวมกับ work_status
  // เป็น composite key เดียว (เช่น "in_progress:self") เพื่อให้ StatusGroup แบบ radio เดิมใช้ได้ตรงๆ
  // โดยไม่ต้องเปลี่ยนค่า work_status จริง (ยังเป็นแค่ waiting|in_progress|done เหมือนเดิม ไม่กระทบ
  // จุดอื่นที่เช็ค work_status แบบ exact-match อยู่หลายจุดทั้ง frontend/backend)
  const workStatusCurrentKey =
    p?.work_status === 'in_progress' && p?.work_method
      ? `in_progress:${p.work_method}`
      : p?.work_status;

  const handleWorkStatusSelect = (composite) => {
    let workStatus = composite;
    let workMethod = '';
    if (composite.startsWith('in_progress:')) {
      workStatus = 'in_progress';
      workMethod = composite.slice('in_progress:'.length);
    }
    const patch = computeStatusCascade(p, 'work_status', workStatus);
    if (!patch) return;
    patch.work_method = workMethod;
    applyPatch(patch);
  };

  const handleLegalToggle = (key) => {
    handleStatusSelect(key, !p?.[key]);
  };

  // บันทึก warranty_end_date ตอน blur/Enter/ปิด popup เท่านั้น — ไม่ใช่ทุกครั้งที่ onChange ยิง
  // เพราะ input type="date" ของ browser ยิง onChange พร้อมค่าที่ยังพิมพ์ไม่ครบ (เช่น พิมพ์เลขปีตัวแรก
  // ตัวเดียว) มาด้วย ถ้า save ทันทีจะได้ปีผิดและ popup ปิดก่อนพิมพ์เสร็จ
  const commitWarrantyDate = () => {
    if (warrantyDateDraft !== toDateInput(p?.warranty_end_date)) {
      handleStatusSelect('warranty_end_date', warrantyDateDraft || null);
    }
    setWarrantyDatePopupAnchor(null);
  };

  // ให้ EditableRow กด Enter แล้วเลื่อน focus ไปช่องถัดไปตาม tabOrder ได้ต่อเนื่อง (ดู EditableFieldRegistryContext)
  // ไล่จนหมดช่องกรอกในแผงนี้แล้ว (ไม่มี tabOrder ถัดไปเหลือ) ให้ไปโฟกัสปุ่ม "โปรเจกต์ถัดไป" ต่อ
  // (ถ้ามี — ถ้าเป็นเรคคอร์ดสุดท้ายไม่มี onNext ก็ไม่ต้องทำอะไร)
  const nextButtonRef = useRef(null);
  // ให้ขอบล่างของ NoteBox (คอลัมน์ขวา) ชนกับขอบล่างแถวสวิตช์ "แสดงสูตร" (แถวสุดท้ายของคอลัมน์กลาง)
  // พอดี — พึ่ง flexbox stretch เฉยๆ ไม่พอ เพราะถ้า 3 คอลัมน์ (sidebar/กลาง/ขวา) ตกบรรทัดกันเอง
  // (flexWrap:'wrap' ตอนพื้นที่ไม่พอ) หรือ sidebar สูงกว่าคอลัมน์กลาง ความสูงที่ stretch มาจะไม่ตรงกับ
  // ความสูง "จริง" ของคอลัมน์กลางที่เนื้อหาจบอยู่ตรงแถวนี้ — วัดความสูงจริงด้วย ResizeObserver แทน
  // แล้วบังคับ minHeight ให้คอลัมน์ขวาเท่ากันเป๊ะ
  const middleColRef = useRef(null);
  const [middleColHeight, setMiddleColHeight] = useState(undefined);
  useEffect(() => {
    const el = middleColRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([entry]) => setMiddleColHeight(entry.contentRect.height));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // เฉพาะตอน dev — วัดความกว้างจริงของ 3 คอลัมน์ (sidebar/กลาง/ขวา) ด้วย ResizeObserver โชว์เป็น
  // ตัวหนังสือลอย (position:fixed, ไม่มี background/border) กลางล่างจอ ไม่ยุ่งกับ sx ของกล่องจริงเลย
  // นอกจาก ref เฉยๆ (เหมือน pattern DevWinBadge/DevTabBadge ที่มีอยู่แล้วในระบบ)
  const sidebarColRef = useRef(null);
  const rightColRef = useRef(null);
  const [debugColWidths, setDebugColWidths] = useState({});
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return undefined;
    const targets = [
      ['sidebar', sidebarColRef.current],
      ['middle', middleColRef.current],
      ['right', rightColRef.current],
    ].filter(([, el]) => el);
    if (!targets.length) return undefined;
    const ro = new ResizeObserver((entries) => {
      setDebugColWidths((prev) => {
        const next = { ...prev };
        entries.forEach((entry) => {
          const found = targets.find(([, el]) => el === entry.target);
          if (found) next[found[0]] = Math.round(entry.contentRect.width);
        });
        return next;
      });
    });
    targets.forEach(([, el]) => ro.observe(el));
    return () => ro.disconnect();
  }, []);
  const fieldRegistryMapRef = useRef(new Map());
  const fieldRegistry = useMemo(
    () => ({
      register: (order, fn) => fieldRegistryMapRef.current.set(order, fn),
      unregister: (order) => fieldRegistryMapRef.current.delete(order),
      activateNext: (order) => {
        for (let i = order + 1; i <= order + 10; i += 1) {
          const fn = fieldRegistryMapRef.current.get(i);
          if (fn) {
            fn();
            return;
          }
        }
        if (onNext) {
          nextButtonRef.current?.focus();
        }
      },
    }),
    [onNext],
  );

  // เรคคอร์ดใหม่เพิ่ง mount (ProjectPopup ใส่ key={id} unmount+remount ทุกครั้งที่เปลี่ยนโปรเจกต์) —
  // ถ้ามาจากการกด Enter ต่อที่ปุ่ม "โปรเจกต์ถัดไป" (ไม่ใช่คลิกเมาส์ธรรมดา) ให้เปิดช่องกรอกแรกให้เองทันที
  useEffect(() => {
    if (consumeAutoFocusFirstField()) {
      fieldRegistry.activateNext(0);
    }
  }, [projectId, fieldRegistry]);

  const [progressBlockedMsg, setProgressBlockedMsg] = useState('');
  const [warrantyDatePopupAnchor, setWarrantyDatePopupAnchor] = useState(null); // popup กรอกวันครบกำหนดรับประกัน
  const [warrantyDateDraft, setWarrantyDateDraft] = useState(''); // ค่าชั่วคราวระหว่างพิมพ์ — ยังไม่ยิง patch จนกว่าจะ blur/Enter
  const [showFormula, setShowFormula] = useState(false);
  const [reviewHistoryOpen, setReviewHistoryOpen] = useState(false);
  // ลากขยาย popup รายการ (resize:both) ตั้งใจให้จำขนาดไว้แค่ระหว่างที่เปิดค้างอยู่เท่านั้น — พอปิด/
  // เปิดใหม่ต้องกลับไปขนาด default เสมอ ทำโดย bump ตัวนี้ทุกครั้งที่เปิด popup แล้วใช้เป็น key ของ
  // Dialog แต่ละอัน (key เปลี่ยน → React unmount/remount Paper ใหม่ ล้าง inline style ที่ browser
  // จำไว้จากการลากครั้งก่อนทิ้งไปด้วย)
  const [resizePopupTick, setResizePopupTick] = useState(0);
  const [pendingItemsOpen, setPendingItemsOpen] = useState(false);
  // รายการตั้งเบิกที่ยังไม่อนุมัติ/ยังไม่จ่าย (ไม่ใช่ SUCCESS และไม่ใช่ REJECT) — เกณฑ์เดียวกับ
  // getExpectedValue() ที่ใช้คำนวณแถว 9 เป๊ะ (ไม่งั้นตัวเลขในป้ายกับรายการในป็อปอัพจะไม่ตรงกัน)
  const pendingExpenseBudgetIds = new Set(
    (p?.budget || []).map((b) => String(b?._id || b?.id)),
  );
  const pendingExpenseItems = (p?.expenses || [])
    .filter(
      (e) =>
        e?.status !== 'SUCCESS' &&
        e?.status !== 'REJECT' &&
        pendingExpenseBudgetIds.has(String(e?.budget)),
    )
    .sort((a, b) => new Date(b?.date || 0) - new Date(a?.date || 0));

  const [paidItemsOpen, setPaidItemsOpen] = useState(false);
  // รายการเบิกที่จ่ายแล้ว (SUCCESS) ที่มาของยอด "BG PAID" (row 7) — เกณฑ์เดียวกับ
  // getPaidValue() เป๊ะ (ไม่งั้นตัวเลขในป้ายกับรายการในป็อปอัพจะไม่ตรงกัน)
  const paidExpenseItems = (p?.expenses || [])
    .filter(
      (e) =>
        e?.status === 'SUCCESS' && pendingExpenseBudgetIds.has(String(e?.budget)),
    )
    .sort((a, b) => (Number(b?.price) || 0) - (Number(a?.price) || 0));
  // เงินรอคืน (refund_items ยังไม่คืน) ที่ getPaidValue() หักออกจาก BG PAID — โชว์เป็นรายการติดลบต่อท้าย
  // popup row 7 ให้เห็นว่าหักอะไรไปบ้าง และ SUM ตรงกับตัวเลขในแถว 7
  const paidRefundItems = (p?.budget || [])
    .flatMap((b) => {
      const budgetId = String(b?._id || b?.id);
      const budgetLabel = `${b?.prefix || ''}${b?.budget_number || ''} ${b?.name || ''}`.trim();
      return (b?.refund_items || [])
        .filter((i) => !i?.refunded)
        .map((i) => ({ ...i, budgetId, budgetLabel }));
    })
    .sort((a, b) => (Number(b?.amount) || 0) - (Number(a?.amount) || 0));

  const [apPendingItemsOpen, setApPendingItemsOpen] = useState(false);
  // รายการ pending_items ที่ยังไม่จ่าย (paid !== true) ของทุกงบย่อย — เกณฑ์เดียวกับ
  // ap_pending_unpaid ที่ backend คำนวณ (project.service.js) เป๊ะ ที่แถว 10 ใช้แสดงผล
  const apPendingItems = (p?.budget || [])
    .flatMap((b) => {
      const budgetId = String(b?._id || b?.id);
      const budgetLabel = `${b?.prefix || ''}${b?.budget_number || ''} ${b?.name || ''}`.trim();
      return (b?.pending_items || [])
        .filter((i) => !i?.paid)
        .map((i) => ({ ...i, budgetId, budgetLabel }));
    })
    .sort((a, b) => (Number(b?.amount) || 0) - (Number(a?.amount) || 0));

  const [bgRemainOpen, setBgRemainOpen] = useState(false);
  // งบเหลือต่องบย่อย (แถว 12 "BGเหลือที่มีสิทธิจ่าย") = ยอดตั้งไว้ต่อรายการ − Paid (expenses SUCCESS
  // ที่ผูกกับงบย่อยนั้นจริง) — เกณฑ์เดียวกับ getPaidValue() ที่ใช้คำนวณ projectedFund/แถว 12 เป๊ะ
  // (จับคู่ผ่าน p.expenses กับ budget id ตรงๆ ไม่ใช้ budget.expenses ที่ฝังมาเอง กันข้อมูลเพี้ยน)
  const bgRemainBreakdown = (p?.budget || []).map((b) => {
    const bId = String(b?._id || b?.id);
    // หักเงินรอคืนของงบย่อยนี้ด้วย เหมือน getPaidValue()
    const refundPendingOfBudget = (b?.refund_items || [])
      .filter((i) => !i?.refunded)
      .reduce((s, i) => s + (Number(i?.amount) || 0), 0);
    const paid = (p?.expenses || []).reduce(
      (acc, e) =>
        acc + (e?.status === 'SUCCESS' && String(e?.budget) === bId ? Number(e?.price) || 0 : 0),
      0,
    ) - refundPendingOfBudget;
    const allocated = Number(b?.cost) || 0;
    return {
      id: bId,
      budgetLabel: `${b?.prefix || ''}${b?.budget_number || ''} ${b?.name || ''}`.trim(),
      allocated,
      paid,
      remaining: allocated - paid,
    };
  }).sort((a, b) => b.remaining - a.remaining);

  // รอเงินคืน(เช่นค่ามัดจำ) ต่องบย่อย (แถว 13) — เกณฑ์เดียวกับ getRefundPendingValue() (refund_items
  // ที่ยังไม่ refunded) กรุ๊ปตามงบย่อยไว้โชว์ breakdown แล้วดับเบิลคลิกแต่ละรายการเปิด
  // RefundItemsModal (P-151) ของงบย่อยนั้นต่อ (ผ่าน onOpenRefundModal ที่ ProjectPopup.js ส่งลงมา)
  const [refundBreakdownOpen, setRefundBreakdownOpen] = useState(false);
  const refundBreakdown = (p?.budget || [])
    .map((b) => {
      const items = (b?.refund_items || []).filter((i) => !i?.refunded);
      const pending = items.reduce((s, i) => s + (Number(i?.amount) || 0), 0);
      return {
        id: String(b?._id || b?.id),
        budgetLabel: `${b?.prefix || ''}${b?.budget_number || ''} ${b?.name || ''}`.trim(),
        pending,
      };
    })
    .filter((r) => r.pending > 0)
    .sort((a, b) => b.pending - a.pending);

  // อัปเดตหน้าจอทันทีแบบ optimistic (คำนวณ toggle เองฝั่ง frontend ตาม logic เดียวกับ backend)
  // แทนที่จะรอผลจาก response ของ PATCH/GET ตามหลัง — เจอเคส response ของ PATCH นี้กลับมาเป็น
  // 204 ไม่มี body บางครั้ง (น่าจะโดน browser/proxy แคชบัง) ทำให้ onLocalUpdate ได้ค่า undefined
  // มาทับของเดิม เช็คบ็อกซ์เลยดูเหมือนกดไม่ติด ทั้งที่ backend บันทึกลง DB สำเร็จจริง
  const handleToggleReviewCheck = async () => {
    const todayStr = dayjs().format('YYYY-MM-DD');
    const prevChecks = p?.review_checks || [];
    const alreadyChecked = prevChecks.some((c) => c.date === todayStr);
    const optimisticChecks = alreadyChecked
      ? prevChecks.filter((c) => c.date !== todayStr)
      : [
          ...prevChecks,
          {
            date: todayStr,
            reviewedBy: me?.userData?._id,
            reviewedByName: authorName || 'ไม่ระบุ',
            reviewedAt: new Date().toISOString(),
          },
        ];
    onLocalUpdate?.({ review_checks: optimisticChecks });
    try {
      await dispatch(actions.projectToggleReviewCheck(projectId));
      onSaved?.();
    } catch (err) {
      console.error('ติ๊กตรวจสอบไม่สำเร็จ', err);
      onLocalUpdate?.({ review_checks: prevChecks });
    }
  };

  // ดาวน์โหลด breakdown ของ row 7,9,10,11,12,13 (แผงคาดว่าต้องจ่ายในอนาคต + BG PAID) รวมเป็น
  // sheet เดียว "OVERALL EXPENSE" เรียงจาก row13 ลงมาถึง row7 — ใช้อาเรย์เดียวกับที่ popup
  // drilldown ของแต่ละ row ใช้แสดงผลอยู่แล้วเป๊ะ
  // (paidExpenseItems/pendingExpenseItems/apPendingItems/bgRemainBreakdown/refundBreakdown)
  // กันตัวเลขในไฟล์ excel ไม่ตรงกับที่เห็นในแดชบอร์ด — row 11 ไม่มีรายการย่อย (เป็นแค่ค่าที่กรอกเอง)
  // เลยออกเป็น title/header/data คงที่ 3 แถว
  const handleExportExpenses = () => {
    const budgetLabelById = new Map(
      (p?.budget || []).map((b) => [
        String(b?._id || b?.id),
        `${b?.prefix || ''}${b?.budget_number || ''} ${b?.name || ''}`.trim(),
      ]),
    );

    const SECTION_GAP = 2;
    const rows = [];
    const addGap = () => {
      for (let i = 0; i < SECTION_GAP; i += 1) rows.push([]);
    };

    rows.push([
      `Budget_Breakdown_${p?.project_number || projectId}_${dayjs().format('YYYY-MM-DD')}`,
    ]);

    // row13 รอเงินคืน
    addGap();
    rows.push(['row13 รอเงินคืน']);
    if (refundBreakdown.length) {
      rows.push(['', '', '', 'งบประมาณ', 'รอเงินคืน']);
      refundBreakdown.forEach((b) => {
        rows.push(['', '', '', b?.budgetLabel || '', Number(b?.pending) || 0]);
      });
    }

    // row12 BG เหลือ
    addGap();
    rows.push(['row12 BG เหลือ']);
    if (bgRemainBreakdown.length) {
      rows.push(['', 'ตั้งไว้', 'ใช้ไปแล้ว', 'งบประมาณ', 'คงเหลือ']);
      bgRemainBreakdown.forEach((b) => {
        rows.push([
          '',
          Number(b?.allocated) || 0,
          Number(b?.paid) || 0,
          b?.budgetLabel || '',
          Number(b?.remaining) || 0,
        ]);
      });
    }

    // row11 ต้องใช้อีก — ไม่มีรายการย่อย เลยจัดคงที่ 3 แถว (title / header / data)
    addGap();
    rows.push(['row11 ต้องใช้อีก']);
    rows.push(['รายการ', '', '', '', 'จำนวนเงิน']);
    rows.push(['ต้องใช้อีก', '', '', '', Number(p?.to_spend) || 0]);

    // row10 AP PENDING
    addGap();
    rows.push(['row10 AP PENDING']);
    if (apPendingItems.length) {
      rows.push(['วันที่', 'รายการ', 'งบประมาณ', 'จำนวนเงิน']);
      apPendingItems.forEach((i) => {
        rows.push([
          i?.createdAt ? dayjs(i.createdAt).format('DD/MM/YYYY') : '',
          i?.name || '',
          i?.budgetLabel || '',
          Number(i?.amount) || 0,
        ]);
      });
    }

    // row9 รอจ่าย
    addGap();
    rows.push(['row9 รอจ่าย']);
    if (pendingExpenseItems.length) {
      rows.push(['วันที่', 'รหัส', 'รายการ', 'งบประมาณ', 'จำนวนเงิน', 'สถานะ']);
      pendingExpenseItems.forEach((e) => {
        rows.push([
          e?.date ? dayjs(e.date).format('DD/MM/YYYY') : '',
          e?.code || '',
          e?.name || '',
          budgetLabelById.get(String(e?.budget)) || '',
          Number(e?.price) || 0,
          EXPENSE_STATUS_LABEL[e?.status] || e?.status || '',
        ]);
      });
    }

    // row7 BG PAID
    addGap();
    rows.push(['row7 BG PAID']);
    if (paidExpenseItems.length || paidRefundItems.length) {
      rows.push(['วันที่', 'รหัส', 'รายการ', 'งบประมาณ', 'จำนวนเงิน']);
      paidExpenseItems.forEach((e) => {
        rows.push([
          e?.date ? dayjs(e.date).format('DD/MM/YYYY') : '',
          e?.code || '',
          e?.name || '',
          budgetLabelById.get(String(e?.budget)) || '',
          Number(e?.price) || 0,
        ]);
      });
      // เงินรอคืนที่หักออกจาก BG PAID (ติดลบ) — เหมือนใน popup row 7
      paidRefundItems.forEach((i) => {
        rows.push([
          i?.createdAt ? dayjs(i.createdAt).format('DD/MM/YYYY') : '',
          'รอเงินคืน',
          i?.name || '',
          i?.budgetLabel || '',
          -(Number(i?.amount) || 0),
        ]);
      });
    }

    const sheet = XLSX.utils.aoa_to_sheet(rows);
    sheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }];
    sheet['!cols'] = [
      { wch: 20 },
      { wch: 20 },
      { wch: 55 },
      { wch: 38 },
      { wch: 12 },
      { wch: 12 },
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'OVERALL EXPENSE');
    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buffer], { type: 'application/octet-stream' });
    saveAs(
      blob,
      `budget_breakdown_${p?.project_number || projectId}_${dayjs().format('YYYY-MM-DD')}.xlsx`,
    );
  };

  const po = p?.include_vat ? (p?.cost || 0) - (p?.vat || 0) : p?.cost || 0;
  const seedBilled = p?.seed_billed_waiting || 0;
  const seedReceived = p?.seed_received || 0;
  const billedCombined = (p?.billed_waiting || 0) + seedBilled;
  const receivedCombined = (p?.received || 0) + seedReceived;
  const poRemaining = po - receivedCombined - billedCombined;

  const paidVal = getPaidValue(p || {});
  const expectedVal = getExpectedValue(p || {});
  const projectedFund = getProjectedFund(p || {});
  const payableBgEligible =
    p?.bg_usage_category === NO_BG_USAGE_KEY ? 0 : Math.max(Number(projectedFund) || 0, 0);
  const refundPending = getRefundPendingValue(p || {});
  // เงินรอคืน (refundPending) ถูกหักอยู่ใน paidVal (getPaidValue) แล้ว ไม่หักซ้ำตรงนี้
  const realProfit =
    po -
    (paidVal +
      expectedVal +
      (Number(p?.ap_pending_unpaid) || 0) +
      (Number(p?.to_spend) || 0) +
      payableBgEligible);
  const { col14, col15, col16, col17 } = getBgColumnValues(p || {});

  // ถ้ายื่นฟ้องแล้ว (legal_lawsuit) งานหยุดชะงัก คิดกำไรจริงแบบง่ายจากเงินที่รับมาจริงหักด้วยที่จ่าย
  // จริงไปแล้วเท่านั้น (row 3 − row 7) แทนสูตรคาดการณ์ปกติที่รวมส่วนที่ยังไม่เกิดขึ้นจริง (รอจ่าย/ต้องใช้
  // อีก/ฯลฯ) เข้ามาด้วย — ใช้ทั้ง row 22 และ Profit EST ใน POProgressBars ให้ตรงกัน (ไม่งั้นกราฟ
  // กับกล่องสรุปจะโชว์ตัวเลขคนละสูตรกัน)
  const isLawsuit = Boolean(p?.legal_lawsuit);
  const row22Profit = isLawsuit ? receivedCombined - paidVal : realProfit;

  // วันครบกำหนดรับประกัน (warranty_end_date) — คุม legal_warranty อัตโนมัติที่ backend อยู่แล้ว
  // ตรงนี้แค่คำนวณไฮไลต์สี (ใกล้/เกินกำหนด) ให้ user เห็นก่อนมันจะเลยกำหนดจริง
  const warrantyEndDate = p?.warranty_end_date;
  const warrantyDaysLeft = warrantyEndDate
    ? dayjs(warrantyEndDate).startOf('day').diff(dayjs().startOf('day'), 'day')
    : null;
  const warrantyUrgency =
    warrantyDaysLeft == null ? null : warrantyDaysLeft < 0 ? 'expired' : warrantyDaysLeft <= 30 ? 'soon' : 'ok';
  const warrantyColor =
    warrantyUrgency === 'expired' ? '#dc2626' : warrantyUrgency === 'soon' ? '#f97316' : WARRANTY_COLOR;

  const targetProfit = (p?.cost || 0) - (p?.total_budget || 0);
  const bgUsedPct = fmtPct(paidVal, p?.total_budget);
  const bgRemainPct = fmtPct(projectedFund, p?.total_budget);
  const overBudgetPct = col14 != null && col14 < 0 ? fmtPct(Math.abs(col14), p?.total_budget) : null;
  // ค่าคงที่ ไม่คำนวณจากข้อมูลโปรเจกต์นี้อีกต่อไป — กันกรอบกว้างไม่เท่ากันข้ามโปรเจกต์ (เล็ก vs ใหญ่)
  const sharedValueWidth = `${SHARED_VALUE_WIDTH_PX}px`;
  const sharedPctWidth = getExpandedWidthFromDefault(
    getLongestText([
      fmtPct(billedCombined, po),
      fmtPct(receivedCombined, po),
      fmtPct(poRemaining, po),
      fmtPct(p?.total_budget, po),
      fmtPct(targetProfit, po),
      bgUsedPct,
      bgRemainPct,
      fmtPct(realProfit, po) || '0.00%',
    ]),
    `${PCT_COL_W}px`,
    PCT_DEFAULT_CH,
    20,
    1,
  );

  const deliveryDate = p?.delivery_date;
  const creditDays = p?.client_id?.credit_days;
  const paymentCycleDay = p?.payment_cycle_day;
  let expectedDate = null;
  if (deliveryDate && creditDays != null && creditDays !== '') {
    const base = new Date(deliveryDate);
    base.setDate(base.getDate() + Number(creditDays));
    if (paymentCycleDay != null && paymentCycleDay !== '') {
      const day = Number(paymentCycleDay);
      const sameMonth = new Date(base.getFullYear(), base.getMonth(), day);
      expectedDate =
        sameMonth >= base
          ? sameMonth
          : new Date(base.getFullYear(), base.getMonth() + 1, day);
    } else {
      expectedDate = base;
    }
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysLeft = expectedDate
    ? Math.round((expectedDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
    : null;

  const todayStr = dayjs().format('YYYY-MM-DD');
  const reviewChecks = p?.review_checks || [];
  const todayReviewCheck = reviewChecks.find((c) => c.date === todayStr);

  return (
    <ShowFormulaContext.Provider value={showFormula}>
    <ColumnWidthContext.Provider value={{ valueWidth: sharedValueWidth, pctWidth: sharedPctWidth }}>
    <EditableFieldRegistryContext.Provider value={fieldRegistry}>
    {/* พื้นหลังทั้งหน้า T-1 (sheet แดชบอร์ด) เป็นสีแดงอ่อนถ้าโปรเจกต์นี้มีคดีฟ้องร้อง (legal_lawsuit)
        — เตือนให้เห็นทันทีตั้งแต่เปิดหน้า ไม่ต้องไล่หาทีละแถว */}
    <Box sx={{ bgcolor: isLawsuit ? '#fef2f2' : 'transparent' }}>
      {/* Header + กราฟ อยู่แถวเดียวกัน — ใช้ flex-basis เดียวกับกริดด้านล่างทุกตัวเลข
          เพื่อให้ขอบซ้าย/ขวาของกราฟตรงกับขอบคอลัมน์ขวา (เอกสารการส่งงาน) เป๊ะ */}
      <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', mb: 1.5 }}>
        <Box sx={{ width: 200, flexShrink: 0 }}>
          <Box
            sx={{
              bgcolor: '#dbeafe',
              border: '1px solid #93c5fd',
              borderRadius: 1,
              px: 2,
              py: 1,
              textAlign: 'center',
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} color="#1e3a8a">
              {p?.project_number || '-'}
            </Typography>
          </Box>
          {p?.oldest_job_date && (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block', textAlign: 'center', mt: 0.3 }}
            >
              Mo-{String(new Date(p.oldest_job_date).getMonth() + 1).padStart(2, '0')} / Yr-{String(new Date(p.oldest_job_date).getFullYear() % 100).padStart(2, '0')}
            </Typography>
          )}
        </Box>

        <Box sx={{ flex: '1 1 280px', minWidth: 260, maxWidth: 420, overflow: 'hidden' }}>
          <TruncatedTooltipText
            text={isLawsuit && p?.customer ? `${p.customer} (${U('ฟ้องร้อง', 'Lawsuit')})` : p?.customer}
            variant="body1"
            fontWeight={700}
            color={isLawsuit ? '#dc2626' : undefined}
          />
          <TruncatedTooltipText text={p?.name} variant="body2" color="#2563eb" />
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mt: 0.3 }}>
            <Box sx={{ width: 'fit-content' }}>
              <EditableRow
                tabOrder={1}
                label={U('ความคืบหน้า', 'Progress')}
                value={p?.progress ?? 0}
                displayValue={`${Number(p?.progress || 0).toFixed(2)}%`}
                type="number"
                min={0}
                max={100}
                bold
                textColor="#1e3a8a"
                center
                valueFontSize="0.7rem"
                labelGap={3}
                borderColor="#64748b"
                fillPct={Number(p?.progress) || 0}
                disabled={p?.work_status === 'done'}
                onDisabledClick={() =>
                  setProgressBlockedMsg(
                    'งานเสร็จ 100% แล้ว ต้องเปลี่ยนสถานะ "ด้านงาน" กลับเป็นดำเนินการก่อน (เลือกว่าใครเป็นผู้ทำ) ถึงจะแก้ % ความคืบหน้าเองได้',
                  )
                }
                onSave={(v) => applyPatch({ progress: v ?? 0 })}
              />
            </Box>
            {col14 != null && (
              <Tooltip
                title={
                  projectedFund < 0
                    ? `เกิน BUDGET แล้ว ${fmt(col14)} (เกิน ${fmtPct(Math.abs(col14), p?.total_budget) || '0.00%'} จาก BG)`
                    : `BG ตอนนี้ยังพอ แต่รวมภาระอนาคตที่ต้องจ่ายอีกแล้วเกิน ${fmt(col14)} (เกิน ${fmtPct(Math.abs(col14), p?.total_budget) || '0.00%'} จาก BG)`
                }
                placement="top"
              >
                <FlagIcon sx={{ fontSize: 15, color: projectedFund < 0 ? '#dc2626' : '#f97316' }} />
              </Tooltip>
            )}
            {!p?.total_budget && (
              <Tooltip title="no Budget Control" placement="top">
                <ErrorOutlineIcon sx={{ fontSize: 15, color: '#dc2626' }} />
              </Tooltip>
            )}
            {p?.has_over_budget_category && (
              <Tooltip
                title={`เกิน BG: ${(p?.over_budget_category_names || []).join(', ')}`}
                placement="top"
              >
                <Box
                  sx={{
                    fontSize: '0.95rem',
                    color: '#e91e8c',
                    lineHeight: 1,
                    cursor: 'default',
                  }}
                >
                  ⚠
                </Box>
              </Tooltip>
            )}
          </Stack>
        </Box>

        <Box sx={{ flex: '1 1 260px', minWidth: 240 }}>
          <POProgressBars
            po={po}
            receivedCombined={receivedCombined}
            billedCombined={billedCombined}
            poRemaining={poRemaining}
            paidVal={paidVal}
            col17={col17}
            totalBudget={p?.total_budget}
            targetProfit={targetProfit}
            realProfit={row22Profit}
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
        {/* Sidebar สถานะ — แก้ไขได้ — พื้นหลังแดงอ่อนด้วยถ้ามีคดีฟ้องร้อง (เหมือนพื้นหลังทั้งหน้าด้านบน) */}
        <Box ref={sidebarColRef} sx={{ width: 200, flexShrink: 0, bgcolor: isLawsuit ? '#fef2f2' : 'transparent', borderRadius: 1 }}>
          <StatusGroup
            title={U('ด้านงาน', 'Work')}
            color="#2563eb"
            options={WORK_OPTIONS_EXPANDED.map((o) => ({
              ...o,
              label: U(o.label, {
                waiting: 'Waiting',
                'in_progress:self': 'Working (Self)',
                'in_progress:hired': 'Working (Outsource)',
                'in_progress:mixed': 'Working (Mixed)',
                done: 'Done 100%',
              }[o.key] || o.label),
            }))}
            currentKey={workStatusCurrentKey}
            onSelect={handleWorkStatusSelect}
            dangerBg={isLawsuit}
          />
          <StatusGroup
            title={U('ด้านการเงิน', 'Finance')}
            color="#d97706"
            options={FINANCE_OPTIONS.map((o) => ({
              ...o,
              label: U(o.label, {
                none: 'Not billed',
                partial: 'Partial',
                billed_waiting: 'Billed',
                last_installment: 'Final due',
                completed_po: 'Paid 100%',
              }[o.key] || o.label),
            }))}
            currentKey={p?.finance_status}
            onSelect={(v) => handleStatusSelect('finance_status', v)}
            getOptionDisabledReason={(opt) => {
              if (opt.key === 'completed_po' && p?.legal_lawsuit) {
                return 'มีการฟ้องร้องอยู่ กดไม่ได้';
              }
              if (opt.key === 'none' && (billedCombined > 0 || receivedCombined > 0)) {
                return 'มีการวางบิล/รับเงินแล้ว กดไม่ได้';
              }
              return '';
            }}
            dangerBg={isLawsuit}
          />
          <StatusGroup
            title={U('หมวดการใช้ BUDGET', 'Budget mode')}
            color="#374151"
            options={BG_USAGE_OPTIONS.map((o) => ({
              ...o,
              label: U(o.label, o.key === NO_BG_USAGE_KEY ? 'BG closed' : o.label),
            }))}
            currentKey={p?.bg_usage_category}
            onSelect={(v) => handleStatusSelect('bg_usage_category', v)}
            getOptionDisabledReason={(opt) =>
              opt.key === NO_BG_USAGE_KEY && p?.work_status !== 'done'
                ? 'งานยังไม่100% กดไม่ได้'
                : ''
            }
            dangerBg={isLawsuit}
          />
          {/* กฎหมาย/ความเสี่ยง — checkbox style — ไม่ได้ใช้ StatusGroup component (custom เอง) เลย
              override สีแดงตอนมีคดีฟ้องร้อง (isLawsuit) ตรงนี้เองแทน prop dangerBg */}
          <Box
            sx={{
              p: 0.65,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: isLawsuit ? '#dc262628' : '#7c3aed28',
              boxShadow: `inset 3px 0 0 ${isLawsuit ? '#dc2626' : '#7c3aed'}`,
              bgcolor: isLawsuit ? '#fef2f2' : '#fafbff',
              mb: 0.6,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontWeight: 700,
                fontSize: '0.67rem',
                color: isLawsuit ? '#dc2626' : '#7c3aed',
                mb: 0.3,
                pb: 0.15,
                borderBottom: `1px solid ${isLawsuit ? '#dc2626' : '#7c3aed'}25`,
              }}
            >
              {U('ด้านนิติกรรม', 'Legal')}
            </Typography>
            <Stack spacing={0}>
              {LEGAL_OPTIONS.map((opt) => {
                const checked = Boolean(p?.[opt.key]);
                const disabledReason =
                  opt.key === 'legal_lawsuit' && p?.finance_status === 'completed_po'
                    ? 'รับเงิน 100% แล้ว ฟ้องร้องไม่ได้'
                    : '';
                const disabled = Boolean(disabledReason);
                const optionNode = (
                  <Box
                    onClick={(e) => {
                      if (disabled) return;
                      // legal_warranty ไม่ toggle ตรงๆ แบบ D/L — เปิด popup กรอกวันครบกำหนดแทน
                      // (legal_warranty derive จากวันที่ที่ backend เสมอ)
                      if (opt.key === 'legal_warranty') {
                        setWarrantyDateDraft(toDateInput(p?.warranty_end_date));
                        setWarrantyDatePopupAnchor(e.currentTarget);
                        return;
                      }
                      handleLegalToggle(opt.key);
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.6,
                      py: 0.1,
                      px: 0.5,
                      borderRadius: 1,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.55 : 1,
                      bgcolor: checked ? `${opt.color}15` : 'transparent',
                      '&:hover': { bgcolor: disabled ? 'transparent' : `${opt.color}10` },
                    }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: 0.5,
                        flexShrink: 0,
                        border: '2px solid',
                        borderColor: checked ? opt.color : '#cbd5e1',
                        bgcolor: checked ? opt.color : 'transparent',
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.72rem',
                        color: checked ? opt.color : 'text.primary',
                        fontWeight: checked ? 700 : 400,
                      }}
                    >
                      {opt.key === 'legal_warranty'
                        ? `Warrant/Guarantee (${
                            warrantyDaysLeft == null ? 'W' : `W${Math.abs(warrantyDaysLeft)}`
                          })`
                        : U(opt.label, opt.labelEn || opt.label)}
                    </Typography>
                  </Box>
                );
                if (disabled) {
                  return (
                    <Tooltip key={opt.key} title={disabledReason} placement="top">
                      <Box component="span" sx={{ display: 'block' }}>
                        {optionNode}
                      </Box>
                    </Tooltip>
                  );
                }
                return <React.Fragment key={opt.key}>{optionNode}</React.Fragment>;
              })}
            </Stack>
            <Popover
              open={Boolean(warrantyDatePopupAnchor)}
              anchorEl={warrantyDatePopupAnchor}
              onClose={commitWarrantyDate}
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <Box sx={{ p: 1 }}>
                <Typography variant="caption" sx={{ display: 'block', mb: 0.4, fontSize: '0.7rem', fontWeight: 600 }}>
                  {U('วันครบกำหนดรับประกัน', 'Warranty end date')}
                </Typography>
                <TextField
                  type="date"
                  size="small"
                  autoFocus
                  value={warrantyDateDraft}
                  onChange={(e) => setWarrantyDateDraft(e.target.value)}
                  onBlur={commitWarrantyDate}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitWarrantyDate();
                    }
                  }}
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.72rem', padding: '4px 8px' } }}
                />
              </Box>
            </Popover>
          </Box>

          <StatusGroup
            title={U('รายงาน/เอกสารส่งงาน', 'Report/Documents')}
            color="#0f172a"
            options={REPORT_OPTIONS.map((o) => ({
              ...o,
              label: U(o.label, {
                none: 'No report required',
                pending: 'Report pending',
                submitted: 'Report sent',
              }[o.key] || o.label),
            }))}
            currentKey={p?.report_status || ''}
            onSelect={(v) => handleStatusSelect('report_status', v)}
            warnEmpty
            dangerBg={isLawsuit}
          />

          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="caption" color="text.secondary">
              {U('ภาระผูกพัน:', 'Commitment:')}
            </Typography>
            {computeHasCommitment(p) ? (
              <Chip
                size="small"
                label={U('มีภาระผูกพัน', 'Committed')}
                sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700, fontSize: '0.7rem' }}
              />
            ) : (
              <Chip
                size="small"
                label={U('ไม่มีภาระผูกพัน', 'No commitment')}
                sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 700, fontSize: '0.7rem' }}
              />
            )}
          </Stack>
        </Box>

        {/* คอลัมน์กลาง: Cashflow + งบประมาณ */}
        <Box ref={middleColRef} sx={{ flex: '1 1 280px', minWidth: 260 }}>
          <SectionHeader>{U('ด้านการCASHFLOW', 'Cashflow')}</SectionHeader>
          <Row num={1} label="PO (No VAT)" value={fmt(po)} bold pct="" />
          <EditableRow
            tabOrder={2}
            num={2}
            label="Invoiced [ VAT incl ]"
            value={seedBilled}
            displayValue={fmt(billedCombined)}
            valueFontSize="0.78rem"
            hint={seedBilled ? `(ตั้งต้น ${fmt(seedBilled)})` : ''}
            min={0}
            bold={billedCombined > 0}
            textColor={billedCombined > 0 ? '#16a34a' : undefined}
            pct={fmtPct(billedCombined, po)}
            onSave={(v) => {
              const val = v ?? 0;
              const patch = { seed_billed_waiting: val };
              const newBilledCombined = (p?.billed_waiting || 0) + val;
              const financeSync = computeFinanceSync(p, po, newBilledCombined, receivedCombined);
              if (financeSync) Object.assign(patch, financeSync);
              if (!patch.work_status) {
                const workBump = computeWorkAutoBump(p, newBilledCombined, receivedCombined);
                if (workBump) Object.assign(patch, workBump);
                else {
                  const workRevert = computeWorkAutoRevert(p, newBilledCombined, receivedCombined);
                  if (workRevert) Object.assign(patch, workRevert);
                }
              }
              applyPatch(patch);
            }}
            labelTooltip="= อัตโนมัติ(จากใบวางบิล) + ค่าตั้งต้นที่กรอกในช่องนี้"
          />
          <EditableRow
            tabOrder={3}
            num={3}
            label={U('รับเงินมาแล้ว', 'Received')}
            value={seedReceived}
            displayValue={fmt(receivedCombined)}
            valueFontSize="0.78rem"
            hint={seedReceived ? `(ตั้งต้น ${fmt(seedReceived)})` : ''}
            min={0}
            underline="single"
            pct={fmtPct(receivedCombined, po)}
            onSave={(v) => {
              const val = v ?? 0;
              const patch = { seed_received: val };
              const newReceivedCombined = (p?.received || 0) + val;
              const financeSync = computeFinanceSync(p, po, billedCombined, newReceivedCombined);
              if (financeSync) Object.assign(patch, financeSync);
              if (!patch.work_status) {
                const workBump = computeWorkAutoBump(p, billedCombined, newReceivedCombined);
                if (workBump) Object.assign(patch, workBump);
                else {
                  const workRevert = computeWorkAutoRevert(p, billedCombined, newReceivedCombined);
                  if (workRevert) Object.assign(patch, workRevert);
                }
              }
              applyPatch(patch);
            }}
            labelTooltip="= อัตโนมัติ(จากใบเสร็จ) + ค่าตั้งต้นที่กรอกในช่องนี้"
          />
          <Row
            num={4}
            label={U('ยอดค้างส่งงานจาก PO', 'PO Remaining')}
            value={fmt(poRemaining)}
            valueFontSize="0.78rem"
            underline="double"
            pct={fmtPct(poRemaining, po)}
            formula="= 1 − 2 − 3"
          />

          <SectionHeader>{U('ด้านการควบคุมงบประมาณ', 'Budget Control')}</SectionHeader>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.3, fontSize: '0.75rem', color: '#000', textDecoration: 'underline' }}>
            -ปัจจุบันจ่ายไปแล้ว
          </Typography>
          <Box sx={{ border: '1.5px solid #7dd3fc', borderRadius: 1, ml: -1, pl: 1 }}>
            <Row
              num={5}
              label="Profit Target"
              value={fmt(targetProfit)}
              valueFontSize="0.78rem"
              labelPl={1}
              textColor="#000"
              pct={fmtPct(targetProfit, po)}
              pctSx={
                po && (targetProfit / po) * 100 < 30
                  ? {
                    color: '#dc2626',
                      bgcolor: '#fef9c3',
                      px: 0.6,
                      py: 0.1,
                      borderRadius: 0,
                      fontWeight: 700,
                    }
                  : undefined
              }
              formula="= 1 − 6"
            />
            <Row
              num={6}
              label={U('BG ที่ตั้งไว้', 'Budget Set')}
              value={fmt(p?.total_budget)}
              valueFontSize="0.78rem"
              labelPl={1}
              textColor="#000"
              pct={fmtPct(p?.total_budget, po)}
            />
          </Box>
          <Row
            num={7}
            label="BG PAID"
            value={paidVal ? fmt(Math.abs(paidVal)) : '-'}
            valueFontSize="0.78rem"
            labelPl={1}
            red
            pct={bgUsedPct}
            labelTooltip="= รวมยอดจ่ายจริง (SUCCESS) ของทุกงบย่อยในโปรเจกต์นี้ − เงินรอคืน (13)"
            onValueDoubleClick={() => {
              setResizePopupTick((t) => t + 1);
              setPaidItemsOpen(true);
            }}
          />
          <Row
            num={8}
            label={U('รายการตั้งเบิกที่รอจ่าย', 'Pending Expenses')}
            value={fmt(expectedVal)}
            valueFontSize="0.78rem"
            labelPl={1}
            red
            sharedWidth
            underline="single"
            labelTooltip="= รวมรายการตั้งเบิกที่ยังไม่อนุมัติ/ยังไม่จ่าย (ไม่ใช่ SUCCESS และไม่ใช่ REJECT)"
            onValueDoubleClick={() => {
              setResizePopupTick((t) => t + 1);
              setPendingItemsOpen(true);
            }}
          />
          <Row
            num={9}
            label={
              <Box
                component="span"
                sx={{ color: projectedFund > 0 ? '#16a34a' : '#dc2626' }}
              >
                BG Remain now
              </Box>
            }
            value={fmt(projectedFund)}
            bold
            labelPl={1}
            textColor={projectedFund > 0 ? '#16a34a' : '#dc2626'}
            pct={bgRemainPct}
            underline="double"
            formula="= 6 − 7 − 8"
          />

          <Typography variant="caption" sx={{ display: 'block', mb: 0.3, mt: 0.8, fontSize: '0.75rem', color: '#000', textDecoration: 'underline' }}>
            -คาดว่าต้องจ่ายในอนาคต
          </Typography>
          <Row
            num={10}
            label={U('AP PENDING ตามงบย่อย', 'AP Pending')}
            value={fmt(p?.ap_pending_unpaid)}
            valueFontSize="0.78rem"
            labelPl={1}
            red
            sharedWidth
            onValueDoubleClick={() => {
              setResizePopupTick((t) => t + 1);
              setApPendingItemsOpen(true);
            }}
          />
          <EditableRow
            tabOrder={4}
            num={11}
            label={U('ต้องใช้อีก', 'To Spend')}
            value={p?.to_spend}
            displayValue={fmt(p?.to_spend)}
            valueFontSize="0.78rem"
            red
            min={0}
            labelPl={1}
            sharedWidth
            onSave={(v) => applyPatch({ to_spend: v ?? 0 })}
          />
          <Row
            num={12}
            label={U('BGเหลือที่มีสิทธิจ่าย', 'Eligible BG')}
            value={fmt(payableBgEligible)}
            valueFontSize="0.78rem"
            labelPl={1}
            sharedWidth
            formula="= ถ้าเลือกไม่ใช้ BG แล้ว = 0, ไม่งั้น = max(9, 0)"
            onValueDoubleClick={() => {
              setResizePopupTick((t) => t + 1);
              setBgRemainOpen(true);
            }}
          />
          <Row
            num={13}
            label={U('รอเงินคืน(เช่นค่ามัดจำ)', 'Refund Pending')}
            value={fmt(refundPending)}
            valueFontSize="0.78rem"
            labelPl={1}
            green={refundPending > 0}
            underline="single"
            sharedWidth
            formula="= รวมเงินมัดจำ/เงินคืนที่ยังไม่ได้คืนจากทุกงบย่อย (หักออกจาก 7 แล้ว)"
            onValueDoubleClick={() => {
              setResizePopupTick((t) => t + 1);
              setRefundBreakdownOpen(true);
            }}
          />
          <Row
            num={14}
            label={U('สรุปอนาคตต้องจ่ายอีกรวม', 'Total Payable')}
            hint={col17 < 0 ? '( ได้เงินคืนระบบ )' : ''}
            hintSx={{ fontSize: '0.78rem', fontWeight: 700, color: '#16a34a' }}
            value={fmt(col17)}
            valueFontSize="0.8rem"
            bold
            textColor={col17 > 0 ? '#d946ef' : col17 < 0 ? '#16a34a' : undefined}
            underline="double"
            sharedWidth
            formula="= 10 + 11 + 12"
          />

          {/* ตรวจสอบรายวัน (ตรวจได้ทุกวันรวมวันอาทิตย์) — ติ๊กแล้วบันทึกเป็น log ไม่ทับของเก่า
              วางไว้ใต้ป้ายสรุปกำไรคาดการณ์ ชิดซ้ายเสมอกับป้ายนั้น */}
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
            <Checkbox
              checked={Boolean(todayReviewCheck)}
              onChange={handleToggleReviewCheck}
              size="small"
              sx={{ p: 0.3 }}
            />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: todayReviewCheck ? '#166534' : 'text.secondary',
              }}
            >
              {todayReviewCheck
                ? `${U('ตรวจสอบแล้ววันนี้ โดย', 'Reviewed today by')} ${todayReviewCheck.reviewedByName}`
                : U('ยังไม่ได้ตรวจสอบวันนี้', 'Not reviewed today')}
            </Typography>
            <Button
              size="small"
              onClick={() => setReviewHistoryOpen(true)}
              sx={{ fontSize: '0.68rem', minWidth: 0, py: 0.1 }}
            >
              {U('ดูประวัติ', 'History')}
            </Button>
            {/* สวิตช์ "แสดงสูตร" — เปิดแล้วทุกรายการที่มีเลขกำกับ (RowNum) จะโชว์สูตรที่มาไว้ใต้แถวตัวเอง
                อ้างอิงเลขของรายการอื่นในแผงนี้ (เช่น "= 1 − 2 − 3") */}
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showFormula}
                  onChange={(e) => setShowFormula(e.target.checked)}
                />
              }
              label={
                <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                  {U('แสดงสูตร', 'Show formulas')}
                </Typography>
              }
              sx={{ m: 0 }}
            />
            {recenterPopup && (
              <Tooltip title="ย้าย popup กลับตำแหน่งเดิม" placement="top">
                <IconButton
                  size="small"
                  onClick={recenterPopup}
                  sx={{ p: 0.5, ml: 3 }}
                >
                  <RestartAltIcon sx={{ fontSize: '1.1rem' }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>

        {/* คอลัมน์ขวา: เอกสารการส่งงาน + คาดว่าต้องจ่ายในอนาคต — flex column ให้ NoteBox
            ท้ายคอลัมน์ขยายลงเต็มความสูงที่ stretch มาจากคอลัมน์อื่น กันพื้นที่ว่างด้านล่าง — บังคับ
            minHeight ให้เท่ากับความสูงจริงของคอลัมน์กลาง (middleColHeight, วัดด้วย ResizeObserver
            ด้านบน) แทนพึ่ง flexbox stretch เฉยๆ เพราะ stretch เพี้ยนได้ถ้า 3 คอลัมน์ตกบรรทัดแยกกัน
            หรือ sidebar สูงกว่าคอลัมน์กลาง — ให้ขอบล่าง NoteBox ชนกับแถวสวิตช์ "แสดงสูตร" พอดีเสมอ */}
        <Box ref={rightColRef} sx={{ flex: '1 1 260px', minWidth: 240, minHeight: middleColHeight, display: 'flex', flexDirection: 'column' }}>
          <SectionHeader>{U('ด้านเอกสารการส่งงาน', 'Delivery Documents')}</SectionHeader>
          <EditableRow
            tabOrder={5}
            num={15}
            label={U('ส่งงาน วันที่', 'Delivery Date')}
            type="date"
            value={toDateInput(deliveryDate)}
            displayValue={fmtDate(deliveryDate) || '-'}
            valueFontSize="0.78rem"
            sharedWidth
            onSave={(v) => applyPatch({ delivery_date: v })}
          />
          <Row
            num={16}
            label={U('เครดิต (วัน)', 'Credit (Days)')}
            value={creditDays != null && creditDays !== '' ? creditDays : '-'}
            valueFontSize="0.78rem"
            sharedWidth
          />
          <EditableRow
            tabOrder={6}
            num={17}
            label={U('รอบจ่ายเงิน', 'Pay Cycle')}
            value={paymentCycleDay}
            displayValue={paymentCycleDay != null && paymentCycleDay !== '' ? paymentCycleDay : '-'}
            valueFontSize="0.78rem"
            min={1}
            max={31}
            sharedWidth
            onSave={(v) => applyPatch({ payment_cycle_day: v })}
          />
          <Row
            num={18}
            label={U('รับเงิน วันที่', 'Payment Date')}
            value={fmtDate(expectedDate) || '-'}
            valueFontSize="0.78rem"
            sharedWidth
            formula="= 15 + 16 (วัน) ปรับให้ตรงวันที่ 17 ของรอบถัดไปถ้ากำหนดไว้"
          />
          <Row
            num={19}
            label={U('รับเงิน อีก (วัน)', 'Days to Payment')}
            value={
              daysLeft == null
                ? '-'
                : daysLeft < 0
                ? `เกิน ${-daysLeft}`
                : daysLeft === 0
                ? 'วันนี้'
                : `+${daysLeft}`
            }
            valueFontSize="0.78rem"
            red={daysLeft != null && daysLeft < 0}
            sharedWidth
            formula="= 18 − วันนี้"
          />

          <SectionHeader>{U('สรุป', 'Summary')}</SectionHeader>
          <Box>
            <Row num={20} label={U('สรุป BG คงเหลือ', 'BG Remaining')} value={fmt(col15)} bold={col15 > 0} formula="= max(9 − 14, 0)" />
            {col14 != null ? (
              <Box sx={{ bgcolor: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 0.9, px: 0.5 }}>
                <Row
                  num={21}
                  label={U('สรุป BG เกิน (เกินจากBGที่ตั้งไว้)', 'BG Overrun')}
                  value={fmt(col14)}
                  pct={overBudgetPct}
                  red
                  bold
                  formula="= 9 − 14 (โชว์เฉพาะติดลบ)"
                />
              </Box>
            ) : (
              <Row
                num={21}
                label={U('สรุป BG เกิน', 'BG Overrun')}
                value="-"
                formula="= 9 − 14 (โชว์เฉพาะติดลบ)"
              />
            )}
          </Box>

          {/* แถบสีสรุปกำไรคาดการณ์ — เขียว = กำไร, แดง = ขาดทุน
              ใช้ Row component เดียวกับ row 21 ตรงๆ (แทนที่เคย hand-roll Stack/Typography แยกเอง)
              กันปัญหา padding/font/สไตล์เพี้ยนไม่ตรงกันจากการต้องคอยแก้ทีละจุดให้เท่ากันเอง */}
          <Box
            sx={{
              mt: 0.6,
              px: 0.5,
              borderRadius: 0.9,
              bgcolor: row22Profit >= 0 ? '#dcfce7' : '#fee2e2',
              border: '1px solid',
              borderColor: row22Profit >= 0 ? '#86efac' : '#fca5a5',
            }}
          >
            <Row
              num={22}
              label={
                isLawsuit
                  ? U('สรุป กำไรคาดการณ์ (ยื่นฟ้อง)', 'Projected Profit (Lawsuit)')
                  : U('สรุป กำไรคาดการณ์', 'Projected Profit')
              }
              value={fmt(row22Profit)}
              pct={fmtPct(row22Profit, po) || '0.00%'}
              textColor={row22Profit >= 0 ? '#166534' : '#991b1b'}
              bold
              formula={isLawsuit ? '= 3 − 7 (ยื่นฟ้อง)' : '= 1 − 7 − 14'}
            />
          </Box>

          <NoteBox
            value={p?.dashboard_note}
            authorName={authorName}
            uiLang={uiLang}
            onSave={(v) => applyPatch({ dashboard_note: v })}
          />
        </Box>
      </Box>

      {process.env.NODE_ENV === 'development' && (
        // ลอยอิสระกลางล่างจอ (position:fixed) แยกขาดจาก 3 คอลัมน์จริงด้านบนโดยสิ้นเชิง — ไม่ได้ซ้อน
        // อยู่ในกล่องไหนเลย กล่องจริงโดนแตะแค่ ref (ดู sidebarColRef/middleColRef/rightColRef) เพื่อวัด
        // ขนาดเท่านั้น ไม่แก้ sx/layout ของกล่องจริงแม้แต่บรรทัดเดียว
        <Typography
          variant="caption"
          sx={{
            position: 'fixed',
            left: '50%',
            bottom: 92, // สูงกว่าป้ายตัวกรอง (bottom:8) และปุ่มเลื่อนก่อนหน้า/ถัดไป (bottom:56) กันซ้อนทับ
            transform: 'translateX(-50%)',
            zIndex: 9999,
            fontFamily: 'monospace',
            fontSize: '0.7rem',
            color: '#334155',
            pointerEvents: 'none',
          }}
        >
          sidebar {debugColWidths.sidebar ?? '-'}px · กลาง {debugColWidths.middle ?? '-'}px · ขวา {debugColWidths.right ?? '-'}px
        </Typography>
      )}

      {/* ปุ่มเลื่อนก่อนหน้า/ถัดไป — ลอยสูงขึ้นมาต่างหาก (bottom เยอะกว่า)
          ไม่ติดกับป้ายตัวกรองด้านล่าง แยก sticky คนละกล่องกัน คนละ bottom offset */}
      {(onPrev || onNext) && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 56,
            display: 'flex',
            justifyContent: 'center',
            mt: 0.5,
          }}
        >
          <Stack direction="row" spacing={3} alignItems="center">
            <Tooltip title={onPrev ? 'โปรเจกต์ก่อนหน้า' : 'ไม่มีโปรเจกต์ก่อนหน้า'} placement="top">
              <Box component="span">
                <IconButton onClick={onPrev} disabled={!onPrev} sx={{ opacity: onPrev ? 1 : 0.35 }}>
                  <NavigateBeforeIcon sx={{ fontSize: '1.8rem' }} />
                </IconButton>
              </Box>
            </Tooltip>
            {navIndex >= 0 && navTotal > 0 && (
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap' }}
              >
                {navIndex + 1} OF {navTotal}
              </Typography>
            )}
            <Tooltip title={onNext ? 'โปรเจกต์ถัดไป' : 'ไม่มีโปรเจกต์ถัดไป'} placement="top">
              <Box component="span">
                <IconButton
                  ref={nextButtonRef}
                  onClick={onNext}
                  onKeyDown={(e) => {
                    // มาจาก Enter (ไม่ใช่คลิกเมาส์ธรรมดา) — เรคคอร์ดถัดไปที่กำลังจะ mount จะได้เปิด
                    // ช่องกรอกแรก (tabOrder=1) ให้เองทันที ไล่ต่อเนื่องเป็นห่วงโซ่เดียวกับตอนกด Enter ไล่ทีละช่อง
                    if (e.key === 'Enter' && onNext) requestAutoFocusFirstField();
                  }}
                  disabled={!onNext}
                  sx={{ opacity: onNext ? 1 : 0.35 }}
                >
                  <NavigateNextIcon sx={{ fontSize: '1.8rem' }} />
                </IconButton>
              </Box>
            </Tooltip>
          </Stack>
        </Box>
      )}

      {/* ป้ายตัวกรอง P5 (กึ่งกลาง) + ปุ่มดาวน์โหลดรายการเบิกจ่าย (ขวาสุด) — แยกลอยชิดขอบล่างสุด
          ของตัวเองเสมอ ไม่ผูกกับตำแหน่งปุ่มเลื่อนด้านบน ใช้ grid 1fr/auto/1fr กันชิปกึ่งกลางเพี้ยน
          เวลาปุ่มขวามีความกว้างไม่เท่าฝั่งซ้าย (ฝั่งซ้ายว่างเปล่าไว้ให้สมมาตรเฉยๆ) */}
      <Box
        sx={{
          position: 'sticky',
          bottom: 8,
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
          px: 1,
        }}
      >
        <Box />
        {filterChips?.length > 0 ? (
          <Stack
            direction="row"
            spacing={0.5}
            justifyContent="center"
            flexWrap="wrap"
            sx={{ rowGap: 0.5, maxWidth: '90%' }}
          >
            {filterChips.map((f) => (
              <Chip
                key={f.key}
                label={f.label}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.68rem',
                  bgcolor: '#cbd5e1',
                  color: '#334155',
                  fontWeight: 600,
                  '& .MuiChip-label': { px: 0.8 },
                }}
              />
            ))}
          </Stack>
        ) : (
          <Box />
        )}
        <Tooltip title="ดาวน์โหลด breakdown row 7,9,10,11,12,13 (Excel)" placement="top">
          <IconButton
            size="small"
            onClick={handleExportExpenses}
            sx={{ justifySelf: 'end' }}
          >
            <FileDownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <DevWinBadge
        num={149}
        name="Project · Review Check History"
        open={reviewHistoryOpen}
        bottom={84}
      />
      <Dialog
        open={reviewHistoryOpen}
        onClose={() => setReviewHistoryOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
            ประวัติการตรวจสอบ
          </Typography>
          {reviewChecks.length === 0 ? (
            <Typography variant="body2" color="text.disabled">
              ยังไม่มีประวัติการตรวจสอบ
            </Typography>
          ) : (
            <Stack spacing={0.75}>
              {[...reviewChecks]
                .sort((a, b) => b.date.localeCompare(a.date))
                .map((c) => (
                  <Stack
                    key={c.date}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      py: 0.5,
                      px: 1,
                      borderRadius: 1,
                      bgcolor: '#f8fafc',
                    }}
                  >
                    <Typography variant="body2">
                      {dayjs(c.date).format('DD/MM/YYYY')} ({THAI_WEEKDAYS[dayjs(c.date).day()]})
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {c.reviewedByName || 'ไม่ระบุ'}
                    </Typography>
                  </Stack>
                ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReviewHistoryOpen(false)}>ปิด</Button>
        </DialogActions>
      </Dialog>

      <DevWinBadge
        num={147}
        name="Project · Pending Expense Items (row 9)"
        open={pendingItemsOpen}
        bottom={84}
      />
      <Dialog
        key={`pending-items-${resizePopupTick}`}
        open={pendingItemsOpen}
        onClose={() => setPendingItemsOpen(false)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            resize: 'both',
            overflow: 'auto',
            width: 480,
            minWidth: 400,
            minHeight: 300,
            maxWidth: 'none',
          },
        }}
      >
        <DialogContent sx={{ pt: 2.5 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1.5, px: 1, pb: '1px', borderBottom: '3px double currentColor' }}
          >
            <Typography variant="subtitle2" fontWeight={700}>
              รายการตั้งเบิกที่รอจ่าย
            </Typography>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#dc2626' }}>
              <Box component="span" sx={{ mr: 3, color: 'text.primary' }}>SUM</Box>
              {fmt(pendingExpenseItems.reduce((s, e) => s + (Number(e?.price) || 0), 0))}
            </Typography>
          </Stack>
          {pendingExpenseItems.length === 0 ? (
            <Typography variant="body2" color="text.disabled">
              ไม่มีรายการค้างจ่าย
            </Typography>
          ) : (
            <Stack spacing={0.75}>
              {pendingExpenseItems.map((exp) => (
                <Stack
                  key={exp?._id || exp?.id}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                  onDoubleClick={() => onOpenExpenseDetail?.(exp?._id || exp?.id)}
                  sx={{
                    py: 0.5,
                    px: 1,
                    borderRadius: 1,
                    bgcolor: '#f8fafc',
                    cursor: onOpenExpenseDetail ? 'pointer' : 'default',
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap sx={{ fontSize: '0.75rem' }}>
                      {exp?.name || '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <Box component="span" sx={{ color: 'magenta' }}>
                        {exp?.date ? dayjs(exp.date).format('DD/MM/YYYY') : '-'}
                      </Box>
                      {exp?.code ? ` · ${exp.code}` : ''}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', fontSize: '0.75rem' }}
                  >
                    {fmt(exp?.price || 0)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPendingItemsOpen(false)}>ปิด</Button>
        </DialogActions>
      </Dialog>

      <DevWinBadge
        num={153}
        name="Project · Paid Expense Items (row 7)"
        open={paidItemsOpen}
        bottom={84}
      />
      <Dialog
        key={`paid-items-${resizePopupTick}`}
        open={paidItemsOpen}
        onClose={() => setPaidItemsOpen(false)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            resize: 'both',
            overflow: 'auto',
            width: 480,
            minWidth: 400,
            minHeight: 300,
            maxWidth: 'none',
          },
        }}
      >
        <DialogContent sx={{ pt: 2.5 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1.5, px: 1, pb: '1px', borderBottom: '3px double currentColor' }}
          >
            <Typography variant="subtitle2" fontWeight={700}>
              รายการเบิกที่จ่ายแล้ว (BG PAID)
            </Typography>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#dc2626' }}>
              <Box component="span" sx={{ mr: 3, color: 'text.primary' }}>SUM</Box>
              {fmt(
                paidExpenseItems.reduce((s, e) => s + (Number(e?.price) || 0), 0) -
                  paidRefundItems.reduce((s, i) => s + (Number(i?.amount) || 0), 0),
              )}
            </Typography>
          </Stack>
          {paidExpenseItems.length === 0 && paidRefundItems.length === 0 ? (
            <Typography variant="body2" color="text.disabled">
              ไม่มีรายการที่จ่ายแล้ว
            </Typography>
          ) : (
            <Stack spacing={0.75}>
              {paidExpenseItems.map((exp) => (
                <Stack
                  key={exp?._id || exp?.id}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                  onDoubleClick={() => onOpenExpenseDetail?.(exp?._id || exp?.id)}
                  sx={{
                    py: 0.5,
                    px: 1,
                    borderRadius: 1,
                    bgcolor: '#f8fafc',
                    cursor: onOpenExpenseDetail ? 'pointer' : 'default',
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap sx={{ fontSize: '0.75rem' }}>
                      {exp?.name || '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <Box component="span" sx={{ color: 'magenta' }}>
                        {exp?.date ? dayjs(exp.date).format('DD/MM/YYYY') : '-'}
                      </Box>
                      {exp?.code ? ` · ${exp.code}` : ''}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', fontSize: '0.75rem' }}
                  >
                    {fmt(exp?.price || 0)}
                  </Typography>
                </Stack>
              ))}
              {paidRefundItems.map((item) => (
                <Stack
                  key={`refund-${item?._id}`}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                  onDoubleClick={() => onOpenRefundModal?.(item.budgetId)}
                  sx={{
                    py: 0.5,
                    px: 1,
                    borderRadius: 1,
                    bgcolor: '#f0fdf4',
                    cursor: onOpenRefundModal ? 'pointer' : 'default',
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap sx={{ fontSize: '0.75rem' }}>
                      {item?.name || '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <Box component="span" sx={{ color: '#16a34a', fontWeight: 700 }}>
                        รอเงินคืน
                      </Box>
                      {item?.budgetLabel ? ` · ${item.budgetLabel}` : ''}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', fontSize: '0.75rem', color: '#16a34a' }}
                  >
                    -{fmt(item?.amount || 0)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPaidItemsOpen(false)}>ปิด</Button>
        </DialogActions>
      </Dialog>

      <DevWinBadge
        num={148}
        name="Project · AP Pending Items (row 10)"
        open={apPendingItemsOpen}
        bottom={84}
      />
      <Dialog
        key={`ap-pending-items-${resizePopupTick}`}
        open={apPendingItemsOpen}
        onClose={() => setApPendingItemsOpen(false)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            resize: 'both',
            overflow: 'auto',
            width: 480,
            minWidth: 400,
            minHeight: 300,
            maxWidth: 'none',
          },
        }}
      >
        <DialogContent sx={{ pt: 2.5 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1.5, px: 1, pb: '1px', borderBottom: '3px double currentColor' }}
          >
            <Typography variant="subtitle2" fontWeight={700}>
              AP PENDING ตามงบย่อย
            </Typography>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#dc2626' }}>
              <Box component="span" sx={{ mr: 3, color: 'text.primary' }}>SUM</Box>
              {fmt(apPendingItems.reduce((s, i) => s + (Number(i?.amount) || 0), 0))}
            </Typography>
          </Stack>
          {apPendingItems.length === 0 ? (
            <Typography variant="body2" color="text.disabled">
              ไม่มีรายการค้างจ่าย
            </Typography>
          ) : (
            <Stack spacing={0.75}>
              {apPendingItems.map((item) => (
                <Stack
                  key={item._id}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                  onDoubleClick={() => onOpenPendingModal?.(item.budgetId)}
                  sx={{
                    py: 0.5,
                    px: 1,
                    borderRadius: 1,
                    bgcolor: '#f8fafc',
                    cursor: onOpenPendingModal ? 'pointer' : 'default',
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap sx={{ fontSize: '0.75rem' }}>
                      {item?.name || '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.budgetLabel}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', fontSize: '0.75rem' }}
                  >
                    {fmt(item?.amount || 0)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setApPendingItemsOpen(false)}>ปิด</Button>
        </DialogActions>
      </Dialog>

      <DevWinBadge
        num={146}
        name="Project · BG Remain Breakdown"
        open={bgRemainOpen}
        bottom={84}
      />
      <Dialog
        key={`bg-remain-${resizePopupTick}`}
        open={bgRemainOpen}
        onClose={() => setBgRemainOpen(false)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            resize: 'both',
            overflow: 'auto',
            width: 480,
            minWidth: 400,
            minHeight: 300,
            maxWidth: 'none',
          },
        }}
      >
        <DialogContent sx={{ pt: 2.5 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1.5, px: 1, pb: '1px', borderBottom: '3px double currentColor' }}
          >
            <Typography variant="subtitle2" fontWeight={700}>
              BGเหลือที่มีสิทธิจ่าย — แยกตามงบย่อย
            </Typography>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#dc2626' }}>
              <Box component="span" sx={{ mr: 3, color: 'text.primary' }}>SUM</Box>
              {fmt(bgRemainBreakdown.reduce((s, b) => s + (Number(b?.remaining) || 0), 0))}
            </Typography>
          </Stack>
          {bgRemainBreakdown.length === 0 ? (
            <Typography variant="body2" color="text.disabled">
              โปรเจกต์นี้ยังไม่มี budget
            </Typography>
          ) : (
            <Stack spacing={0.75}>
              {bgRemainBreakdown.map((b) => (
                <Stack
                  key={b.id}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                  sx={{ py: 0.5, px: 1, borderRadius: 1, bgcolor: '#f8fafc' }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap sx={{ fontSize: '0.75rem' }}>
                      {b.budgetLabel || '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ตั้งไว้ {fmt(b.allocated)} − ใช้ไปแล้ว {fmt(b.paid)}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{
                      whiteSpace: 'nowrap',
                      fontVariantNumeric: 'tabular-nums',
                      fontSize: '0.75rem',
                      color: b.remaining >= 0 ? '#16a34a' : '#dc2626',
                    }}
                  >
                    {fmt(b.remaining)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBgRemainOpen(false)}>ปิด</Button>
        </DialogActions>
      </Dialog>

      <DevWinBadge
        num={152}
        name="Project · Refund Pending Breakdown (row 13)"
        open={refundBreakdownOpen}
        bottom={84}
      />
      <Dialog
        key={`refund-breakdown-${resizePopupTick}`}
        open={refundBreakdownOpen}
        onClose={() => setRefundBreakdownOpen(false)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            resize: 'both',
            overflow: 'auto',
            width: 480,
            minWidth: 400,
            minHeight: 300,
            maxWidth: 'none',
          },
        }}
      >
        <DialogContent sx={{ pt: 2.5 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1.5, px: 1, pb: '1px', borderBottom: '3px double currentColor' }}
          >
            <Typography variant="subtitle2" fontWeight={700}>
              รอเงินคืน(เช่นค่ามัดจำ) — แยกตามงบย่อย
            </Typography>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#dc2626' }}>
              <Box component="span" sx={{ mr: 3, color: 'text.primary' }}>SUM</Box>
              {fmt(refundBreakdown.reduce((s, b) => s + (Number(b?.pending) || 0), 0))}
            </Typography>
          </Stack>
          {refundBreakdown.length === 0 ? (
            <Typography variant="body2" color="text.disabled">
              ไม่มีรายการรอเงินคืน
            </Typography>
          ) : (
            <Stack spacing={0.75}>
              {refundBreakdown.map((b) => (
                <Stack
                  key={b.id}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                  onDoubleClick={() => onOpenRefundModal?.(b.id)}
                  sx={{
                    py: 0.5,
                    px: 1,
                    borderRadius: 1,
                    bgcolor: '#f8fafc',
                    cursor: onOpenRefundModal ? 'pointer' : 'default',
                  }}
                >
                  <Typography variant="body2" noWrap sx={{ fontSize: '0.75rem' }}>
                    {b.budgetLabel || '-'}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', fontSize: '0.75rem' }}
                  >
                    {fmt(b.pending)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRefundBreakdownOpen(false)}>ปิด</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(progressBlockedMsg)}
        autoHideDuration={4000}
        onClose={() => setProgressBlockedMsg('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="warning" onClose={() => setProgressBlockedMsg('')} sx={{ width: '100%' }}>
          {progressBlockedMsg}
        </Alert>
      </Snackbar>
    </Box>
    </EditableFieldRegistryContext.Provider>
    </ColumnWidthContext.Provider>
    </ShowFormulaContext.Provider>
  );
}
